import { Table, TableCell, TableRow } from '@mui/material';
import { styled } from '@mui/material/styles';

import { PinoProductTitle } from '@tgg/common-types';

import { palette, Theme } from '../../theme';
import { getPictureUrlForTable } from '../../utils/getPictureUrlForTable';
import { getPinoColor } from '../../utils/getPinoColors';

type TableCellProperties = {
    $selectedProduct?: PinoProductTitle;
    $hoveredProduct?: PinoProductTitle;
    $isMBTOptimization?: boolean;
    $isDesktop?: boolean;
    theme: Theme;
    disabled?: boolean;
    $isStudentPinoTable?: boolean;
    $isPinoMembershipTable?: boolean;
};

type ProductHeaderProperties = {
    $selectedProduct?: PinoProductTitle;
    $isKickerOfferRibbon?: boolean;
    $isMBTOptimization?: boolean;
    theme: Theme;
    title: PinoProductTitle;
    disabled?: boolean;
    $isStudentPinoTable?: boolean;
};

type ProductFooterProperties = {
    $selectedProduct?: PinoProductTitle;
    theme: Theme;
    disabled?: boolean;
    isOffPeakProductExists?: boolean;
};

export const StyledTable = styled(Table, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<{
    $isClippedWidth?: boolean;
}>`
    max-width: ${({ $isClippedWidth }) => $isClippedWidth && '47.6rem'};
    margin: 0 auto;
    table-layout: auto;
    border-spacing: ${({ theme }) => theme.spacing(2)} 0;
    border-collapse: separate;
    ${({ theme }: { theme: Theme }) => theme.breakpoints.up('desktop')} {
        border-spacing: ${({ theme }) => theme.spacing(3)} 0;
    }
`;

export const StyledCaption = styled('caption')`
    && {
        padding: 0 ${({ theme }) => theme.spacing(30)};
        margin-bottom: ${({ theme }) => theme.spacing(15)};
        font-family: thegymgroupsans-Headline, sans-serif;
        font-size: 1.4rem;
        line-height: 1.4rem;
        color: ${palette.common.blue};
        caption-side: top;
        ${({ theme }: { theme: Theme }) => theme.breakpoints.up('desktop')} {
            text-align: center;
        }
    }
`;

export const StyledButtonWrapperForIcon = styled('button')<{
    $isMBTOptimization?: boolean;
}>`
    display: ${({ $isMBTOptimization }) => $isMBTOptimization && 'flex'};
    gap: 0.2rem;
    align-items: flex-end;
    padding: 0;
    margin: 2rem auto 0;
    ${({ theme }: { theme: Theme }) => theme.breakpoints.up('desktop')} {
        flex-direction: ${({ $isMBTOptimization }) =>
            $isMBTOptimization ? 'row' : ''};
        gap: ${({ $isMBTOptimization }) =>
            $isMBTOptimization ? '0.2rem' : ''};
        align-items: ${({ $isMBTOptimization }) =>
            $isMBTOptimization ? 'flex-end' : ''};
        margin: 3rem auto 0;
        font-size: 1.6rem;
    }

    color: inherit;
    text-align: ${({ $isMBTOptimization }) => $isMBTOptimization && 'left'};
    vertical-align: middle;
    cursor: pointer;
    background-color: transparent;
    border: none;

    p {
        margin: 0.4rem 0 0;
        font-family: thegymgroupsans-headline, serif;
        text-decoration: underline;
        ${({ theme }: { theme: Theme }) => theme.breakpoints.up('desktop')} {
            margin: 0;
        }
    }

    svg {
        margin-bottom: 0.2rem;
    }
`;

export const StyledProductHeader = styled(TableCell, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<ProductHeaderProperties>`
    position: relative;
    max-width: 9rem;
    padding: 1rem 0 0 0;
    text-transform: lowercase;
    vertical-align: bottom;
    border: none;

    &::after {
        position: absolute;
        left: 0.3rem;
        z-index: 1;
        width: calc(100% - 0.6rem);
        content: '';
        border-bottom: 0.5rem solid ${palette.common.white};
    }

    ${({ disabled }: ProductHeaderProperties) => {
        if (disabled) {
            return `
                opacity: 0.5;
                cursor: not-allowed;
            `;
        }

        return ``;
    }}

    ${({ title, $isMBTOptimization }: ProductHeaderProperties) => {
        const color = getPinoColor({
            productTitle: title,
            isMBTOptimization: $isMBTOptimization,
        });

        return `
            background-color: ${color.primary};
            color: ${palette.common.blue};
        `;
    }}

    ${({ $selectedProduct }: TableCellProperties) => {
        if ($selectedProduct) {
            const color = getPinoColor({ productTitle: $selectedProduct });

            return `
                border-inline-color: ${color.primary};
                border-bottom-color: ${color.primary};
            `;
        }

        return ``;
    }}


    ${({ theme }) => theme.breakpoints.up('desktop')} {
        max-width: 20rem;
    }

    ${({ $isKickerOfferRibbon }: ProductHeaderProperties) =>
        $isKickerOfferRibbon ? 'height: 24rem;' : ''}
`;

export const StylesTableRow = styled(TableRow, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<{
    $showFooter?: boolean;
    $isFirstRow?: boolean;
    $isShowLastRowAfter?: boolean;
}>`
    ${({ $isFirstRow }) =>
        $isFirstRow &&
        `
            td {
                vertical-align: top;

                div {
                    margin-top: 0.35rem;
                }
            }
  `}
    ${({ $showFooter }) =>
        $showFooter
            ? ''
            : `
                  &:last-of-type {
                      td {
                          border-bottom-width: 0.2rem;
                          border-bottom-style: solid;
                          border-color: none;
                      }
                  }
              `}
    ${({ $isShowLastRowAfter }) =>
        $isShowLastRowAfter &&
        `
        td {
        &::after {
            position: absolute;
            bottom: 0;
            left: 0;
            z-index: 1;
            width: 100%;
            height: 0.1rem;
            content: '';
            background: ${palette.common.blue};
        }
    }`}
`;

export const StyledTableCell = styled(TableCell, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<TableCellProperties>`
    position: relative;
    max-width: 9rem;
    padding: 1rem;
    border-inline-width: ${({ theme }) => theme.spacing(2)};
    border-inline-style: solid;
    font-family: thegymgroupsans-headline, serif;
    font-size: 1.2rem;
    color: ${palette.common.blue};
    background-color: ${palette.grey[50]};
    border-bottom: none;

    &::after {
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 1;
        width: 100%;
        height: 0.5rem;
        content: '';
        background: ${palette.common.white};
    }

    a {
        color: ${palette.common.blue};
        text-decoration: underline;
        cursor: pointer;
    }

    ${({ disabled }: TableCellProperties) => {
        if (disabled) {
            return `
                opacity: 0.5;
                cursor: not-allowed;
                color: ${palette.common.blue};
            `;
        }

        return ``;
    }}

    ${({
        $selectedProduct,
        $isMBTOptimization,
        $isDesktop,
    }: TableCellProperties) => {
        if ($selectedProduct) {
            const color = getPinoColor({
                productTitle: $selectedProduct,
                isDesktop: $isDesktop,
                isMBTOptimization: $isMBTOptimization,
            });

            return `
                border-inline-color: ${color.primary};
                background-color: ${color.alternate};
                border-right: 0.3rem solid ${color.primary};
                border-left: 0.3rem solid ${color.primary};
                border-bottom: none;

                &::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    bottom: 0;
                    height: 0.5rem;
                    width: 100%;
                    background: ${palette.common.white};
                    z-index: 1;
                }
            `;
        }

        return `
            border-inline-color: ${palette.common.white};
        `;
    }}

    ${({ $hoveredProduct, $isMBTOptimization }: TableCellProperties) => {
        if ($hoveredProduct) {
            const borderColour = getPinoColor({
                productTitle: $hoveredProduct,
                isMBTOptimization: $isMBTOptimization,
            });

            return `
                border-right: 0.3rem solid ${borderColour.primary};
                border-left: 0.3rem solid ${borderColour.primary};

                &::after {
                    content: '';
                    position: absolute;
                    left: 0;
                    bottom: 0;
                    height: 0.5rem;
                    width: 100%;
                    background: ${palette.common.white};
                    z-index: 1;
                }
            `;
        }

        return ``;
    }}

    ${({ theme }: { theme: Theme }) => theme.breakpoints.up('mobile')} {
        border-inline-width: ${({ theme }) => theme.spacing(3)};
        font-size: ${({ $isStudentPinoTable, $isPinoMembershipTable }) =>
            $isStudentPinoTable ? '1.2rem' : '1.4rem'};
    }

    ${({ theme }: { theme: Theme }) => theme.breakpoints.up('desktop')} {
        border-inline-width: ${({ theme }) => theme.spacing(3)};
        font-size: ${({ $isStudentPinoTable, $isPinoMembershipTable }) =>
            $isStudentPinoTable ? '1.2rem' : '1.4rem'};
    }
`;

export const StyledProductHeaderPrices = styled('div')`
    && {
        padding: 1rem 0;
        margin: 0 0.3rem 0.3rem 0.3rem;
        font-size: 1rem;
        background: ${palette.common.white};
        ${({ theme }: { theme: Theme }) => theme.breakpoints.up('mobile')} {
            font-size: 1.2rem;
        }
    }
`;

export const StyledBoldSpanText = styled('span')`
    display: flex;
    flex-direction: row;
    justify-content: center;
    font-family: thegymgroupsans-Headline, sans-serif;
    font-size: 1.6rem;
    span {
        padding-right: 0.3rem;
        font-family: thegymgroupsans-Bold, sans-serif;
        font-size: 1.2rem;
    }
`;

export const StyledMoText = styled('div')`
    font-family: thegymgroupsans-Headline, sans-serif;
    font-size: 1.2rem;
`;

export const StyledProductHeaderJoiningFee = styled('p')`
    padding: 0;
    margin: 0;
    line-height: 1.8rem;
`;

export const StyledTableFooterCell = styled(StyledTableCell, {
    shouldForwardProp: property =>
        typeof property === 'string' && !property.startsWith('$'),
})<ProductFooterProperties>`
    position: relative;
    padding: 1rem 0;
    ${({ isOffPeakProductExists }: ProductFooterProperties) => `
        clip-path: polygon(
            0 0,
            100% 0,
            100% 18%,
            50% ${isOffPeakProductExists ? '70%' : '94%'},
            0 18%
        );
    `}
    color: ${palette.common.white};
    text-align: center;
    border: none;

    button {
        margin-bottom: 3rem;
    }

    ${({ disabled }: ProductFooterProperties) =>
        disabled &&
        `
        opacity: 0.5;
        cursor: not-allowed;
    `}

    ${({ $selectedProduct, $isMBTOptimization }: TableCellProperties) =>
        $selectedProduct &&
        `
            background-color: ${
                getPinoColor({
                    productTitle: $selectedProduct,
                    isMBTOptimization: $isMBTOptimization,
                }).primary
            };
        `}

    ${({
        $hoveredProduct,
        theme,
        isOffPeakProductExists,
    }: TableCellProperties & ProductFooterProperties) => {
        if ($hoveredProduct) {
            //@ts-ignore
            const pictureURL = getPictureUrlForTable($hoveredProduct);
            const heightValue = `${isOffPeakProductExists ? '105%' : '137%'};`;
            const transformValue = `${isOffPeakProductExists ? 'translateX(-50%)' : 'translate(-50%, 16%)'}`;
            return `
                &::after {
                    content: '';
                    position: absolute;
                    bottom: 1.8rem;
                    left: 50%;
                    transform: ${transformValue};
                    width: 100.8%;
                    background-image: url(${pictureURL});
                    height: ${heightValue};
                    background-size: contain;
                    background-repeat: no-repeat;
                    background-position: center;
                    background-color: transparent;
                    pointer-events: none;
                }
            `;
        }

        return ``;
    }}
`;

export const ExploreBenefitsButtonV3 = styled('button')<{
    $isBenefitsShortened?: boolean;
    $isMBTOptimization?: boolean;
}>`
    display: flex;
    margin: auto;
    margin-top: 2rem;
    font-family: 'thegymgroupsans-Headline';
    font-size: 1.4rem;
    font-weight: 800;
    line-height: 2rem;
    color: inherit;
    text-decoration: underline;
    vertical-align: middle;
    cursor: pointer;
    background-color: transparent;
    border: none;

    svg {
        width: 2rem;
        height: 2rem;
    }
`;
